import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { companyService } from '../../services/companyService';

/**
 * Polls supabase.auth.getUser() until a user with a valid ID is returned,
 * or until maxAttempts is exhausted.
 */
const waitForAuthUser = async (maxAttempts = 10, intervalMs = 150) => {
  for (let i = 0; i < maxAttempts; i++) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (user?.id) return user;
    await new Promise(resolve => setTimeout(resolve, intervalMs));
  }
  return null;
};

/**
 * Polls user_profiles until a row with company_id is found,
 * or until maxAttempts is exhausted.
 */
const waitForProfile = async (userId, maxAttempts = 8, intervalMs = 400) => {
  for (let i = 0; i < maxAttempts; i++) {
    const { data: profile } = await supabase
      ?.from('user_profiles')
      ?.select('id, role, company_id')
      ?.eq('id', userId)
      ?.maybeSingle();
    if (profile?.id) return profile;
    await new Promise(resolve => setTimeout(resolve, intervalMs));
  }
  return null;
};

/**
 * Ensures the invited user is inserted into company_users and user_profiles
 * based on metadata set during inviteUserByEmail.
 */
const ensureInviteAccepted = async (user) => {
  const meta = user?.user_metadata ?? {};
  const appMeta = user?.app_metadata ?? {};

  const companyId = meta?.company_id || appMeta?.company_id || null;
  const role = meta?.role || appMeta?.role || null;
  const siteId = meta?.site_id || appMeta?.site_id || null;

  if (!companyId || !role) {
    return { companyId: null, role: null };
  }

  // Insert into company_users (upsert to avoid duplicates)
  await supabase
    ?.from('company_users')
    ?.upsert(
      { user_id: user?.id, company_id: companyId, role },
      { onConflict: 'user_id,company_id', ignoreDuplicates: true }
    );

  // Insert into site_users if siteId present
  if (siteId) {
    await supabase
      ?.from('site_users')
      ?.upsert(
        { user_id: user?.id, site_id: siteId, role },
        { onConflict: 'user_id,site_id', ignoreDuplicates: true }
      );
  }

  // Ensure user_profiles has role and company_id
  await supabase
    ?.from('user_profiles')
    ?.upsert(
      { id: user?.id, role, company_id: companyId },
      { onConflict: 'id', ignoreDuplicates: false }
    );

  return { companyId, role };
};

const AuthCallback = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('Processing your verification…');
  const [fatalError, setFatalError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const handleCallback = async () => {
      try {
        const search = window.location?.search || '';
        const hash = window.location?.hash || '';

        const hashParams = new URLSearchParams(hash.replace(/^#/, ''));
        const queryParams = new URLSearchParams(search);

        const code = queryParams?.get('code');
        const tokenHash = queryParams?.get('token_hash');
        const otpType = queryParams?.get('type');
        const accessToken = hashParams?.get('access_token') || queryParams?.get('access_token');
        const refreshToken = hashParams?.get('refresh_token') || queryParams?.get('refresh_token');
        const type = hashParams?.get('type') || queryParams?.get('type');

        // ── INVITE FAST-PATH: forward invite tokens to /invite-setup ──────────
        if (tokenHash && otpType === 'invite') {
          if (!cancelled) navigate('/invite-setup' + search, { replace: true });
          return;
        }

        // ── ACCESS TOKEN PATH: Supabase email verification links contain access_token + refresh_token ──
        // This is the primary format for Supabase email confirmation links.
        // Do NOT apply the loop guard here — these tokens are a direct session exchange, not OTP.
        if (accessToken && refreshToken) {
          // Clear any stale loop counter so it doesn't interfere with future flows
          sessionStorage.removeItem('sp_callback_attempts');
          window.history?.replaceState({}, document.title, window.location?.pathname);
          setStatus('Signing you in…');

          const { data: sessionData, error: sessionError } = await supabase?.auth?.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (sessionError || !sessionData?.session) {
            // Try to see if we already have a valid session (e.g. link clicked twice)
            const { data: existingSession } = await supabase?.auth?.getSession();
            if (existingSession?.session?.user) {
              const existingUser = existingSession?.session?.user;
              if (!cancelled) {
                await routeAfterSession(existingUser, type || 'signup', cancelled, setStatus, navigate, existingSession?.session);
              }
              return;
            }
            setFatalError('Sign-in failed. Please try again or request a new verification link.');
            return;
          }

          setStatus('Verifying your account…');
          const verifiedUser = await waitForAuthUser(10, 150);

          if (!verifiedUser) {
            setFatalError('Session expired. Please sign in again.');
            return;
          }

          if (!cancelled) {
            await routeAfterSession(verifiedUser, type || 'signup', cancelled, setStatus, navigate, sessionData?.session);
          }
          return;
        }

        // ── LOOP GUARD: only applies to tokenHash and code flows ──────────────
        const loopKey = 'sp_callback_attempts';
        const loopCount = parseInt(sessionStorage.getItem(loopKey) || '0', 10) + 1;
        sessionStorage.setItem(loopKey, String(loopCount));
        if (loopCount > 3) {
          sessionStorage.removeItem(loopKey);
          setFatalError('Verification failed after multiple attempts. Please try signing in manually.');
          return;
        }

        if (tokenHash && (otpType === 'email' || otpType === 'signup' || otpType === 'email_change' || otpType === 'recovery' || otpType === 'magiclink' || otpType === 'invite')) {
          setStatus('Verifying your email…');
          window.history?.replaceState({}, document.title, window.location?.pathname);

          if (otpType === 'recovery') {
            const { error: verifyError } = await supabase?.auth?.verifyOtp({
              token_hash: tokenHash,
              type: 'recovery',
            });
            if (verifyError) {
              setFatalError('Password reset verification failed. Please request a new reset link.');
              return;
            }
            if (!cancelled) navigate('/reset-password', { replace: true });
            return;
          }

          let verifyOtpType = 'email';
          if (otpType === 'email_change') verifyOtpType = 'email_change';
          else if (otpType === 'magiclink') verifyOtpType = 'magiclink';
          else if (otpType === 'invite') verifyOtpType = 'invite';

          const { data: otpData, error: otpError } = await supabase?.auth?.verifyOtp({
            token_hash: tokenHash,
            type: verifyOtpType,
          });

          if (otpError) {
            // Token is genuinely invalid or expired — check if already have a valid session
            const { data: existingSession } = await supabase?.auth?.getSession();
            if (existingSession?.session?.user) {
              const existingUser = existingSession?.session?.user;
              const resolvedType = otpType || 'signup';
              if (!cancelled) {
                await routeAfterSession(existingUser, resolvedType, cancelled, setStatus, navigate, existingSession?.session);
              }
              return;
            }
            setFatalError('Email verification failed. The link may have expired or already been used.');
            return;
          }

          // verifyOtp succeeded — session may or may not be immediately available
          setStatus('Confirming your session…');
          let confirmedSession = otpData?.session || null;

          // Poll for session if not immediately available
          if (!confirmedSession?.user?.id) {
            for (let attempt = 0; attempt < 15; attempt++) {
              await new Promise(resolve => setTimeout(resolve, 300));
              const { data: sessionCheck } = await supabase?.auth?.getSession();
              if (sessionCheck?.session?.user?.id) {
                confirmedSession = sessionCheck?.session;
                break;
              }
            }
          }

          // Final fallback: try getUser directly
          if (!confirmedSession?.user?.id) {
            const { data: { user: directUser } } = await supabase?.auth?.getUser();
            if (directUser?.id) {
              const { data: sessionCheck } = await supabase?.auth?.getSession();
              confirmedSession = sessionCheck?.session;
            }
          }

          if (!confirmedSession?.user?.id) {
            setFatalError('Session could not be established. Please try signing in.');
            return;
          }

          const verifiedUser = confirmedSession?.user;
          const resolvedType = otpType || 'signup';

          sessionStorage.removeItem(loopKey);
          if (!cancelled) {
            await routeAfterSession(verifiedUser, resolvedType, cancelled, setStatus, navigate, confirmedSession);
          }
          return;
        }

        if (code) {
          setStatus('Verifying your email…');

          const { data: sessionData, error: sessionError } = await supabase?.auth?.exchangeCodeForSession(code);
          window.history?.replaceState({}, document.title, window.location?.pathname);

          if (sessionError || !sessionData?.session) {
            const { data: existingSession } = await supabase?.auth?.getSession();
            if (existingSession?.session?.user) {
              const existingUser = existingSession?.session?.user;
              const resolvedType = type || 'signup';
              if (!cancelled) {
                await routeAfterSession(existingUser, resolvedType, cancelled, setStatus, navigate, existingSession?.session);
              }
              return;
            }
            setFatalError('Email verification failed. Please try again or request a new link.');
            return;
          }

          const verifiedUser = sessionData?.session?.user;
          const resolvedType = type || sessionData?.session?.user?.app_metadata?.action;

          setStatus('Confirming your session…');
          let confirmedSession = null;
          for (let attempt = 0; attempt < 10; attempt++) {
            await new Promise(resolve => setTimeout(resolve, 300));
            const { data: sessionCheck } = await supabase?.auth?.getSession();
            if (sessionCheck?.session?.user?.id) {
              confirmedSession = sessionCheck?.session;
              break;
            }
          }

          if (!confirmedSession) confirmedSession = sessionData?.session;

          sessionStorage.removeItem(loopKey);
          if (!cancelled) {
            await routeAfterSession(verifiedUser, resolvedType || 'signup', cancelled, setStatus, navigate, confirmedSession);
          }
          return;
        }

        // ── NO TOKENS: check for existing session or redirect to login ────────
        // (Remove the old accessToken+refreshToken block that was here — now handled above)
        const { data: { session } } = await supabase?.auth?.getSession();
        if (session?.user) {
          sessionStorage.removeItem(loopKey);
          if (!cancelled) {
            await routeAfterSession(session?.user, 'signup', cancelled, setStatus, navigate, session);
          }
          return;
        }

        if (!cancelled) navigate('/login', { replace: true });
      } catch (err) {
        if (!cancelled) setFatalError('Something went wrong. Please try again.');
      }
    };

    handleCallback();
    return () => { cancelled = true; };
  }, [navigate]);

  if (fatalError) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
        <div className="bg-slate-900 border border-red-500/30 rounded-xl p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
          <h2 className="text-white text-lg font-semibold mb-2">Verification failed</h2>
          <p className="text-slate-400 text-sm mb-6">{fatalError}</p>
          <button
            onClick={() => window.location.href = '/login'}
            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
        <p className="text-slate-300 text-sm">{status}</p>
      </div>
    </div>
  );
};

/**
 * Shared post-session routing logic used by all verification flows.
 * Routes purely based on companies table — no sessionStorage/localStorage flags.
 */
async function routeAfterSession(user, type, cancelled, setStatus, navigate, session = null) {
  if (type === 'recovery') {
    if (!cancelled) navigate('/reset-password', { replace: true });
    return;
  }

  setStatus('Setting up your account…');

  const meta = user?.user_metadata ?? {};
  const appMeta = user?.app_metadata ?? {};
  const metaCompanyId = meta?.company_id || appMeta?.company_id || null;
  const metaRole = meta?.role || appMeta?.role || null;

  const isInviteType = type === 'invite';
  const hasInviteMetadata = !!(metaCompanyId && metaRole);

  if (isInviteType || hasInviteMetadata) {
    // raw_user_meta_data.{company_id, role} stays set forever once an invite
    // was issued, so hasInviteMetadata is true for already-set-up users too.
    // Likewise, type === 'invite' can fire on a setSession path long after the
    // user completed setup. Verify against user_profiles before routing back
    // to /invite-setup, otherwise an already-set-up user lands on the password
    // form and silently overwrites their password via updateUser.
    const { data: existingProfile, error: profileFetchError } = await supabase
      ?.from('user_profiles')
      ?.select('full_name, is_active')
      ?.eq('id', user?.id)
      ?.maybeSingle();

    const setupComplete =
      !profileFetchError &&
      !!(existingProfile?.full_name && existingProfile?.is_active === true);

    if (!setupComplete) {
      if (!cancelled) navigate('/invite-setup', { replace: true });
      return;
    }
    // Setup already complete — fall through to normal role-based routing below.
  }

  setStatus('Loading your profile…');

  let profile = await waitForProfile(user?.id, 10, 300);

  if (profile && !profile?.company_id) {
    await new Promise(resolve => setTimeout(resolve, 800));
    const { data: retryProfile } = await supabase
      ?.from('user_profiles')
      ?.select('id, role, company_id')
      ?.eq('id', user?.id)
      ?.maybeSingle();
    if (retryProfile?.id) profile = retryProfile;
  }

  const role = profile?.role || null;
  const companyId = profile?.company_id || null;

  if (role === 'worker') {
    if (!cancelled) navigate('/worker-today', { replace: true });
    return;
  }
  if (role === 'site_manager') {
    if (!cancelled) navigate('/site-manager-dashboard', { replace: true });
    return;
  }
  if (role === 'client') {
    if (!cancelled) navigate('/client-dashboard', { replace: true });
    return;
  }

  // Admin routing — always read from companies table
  if (role === 'admin') {
    await routeAdmin(user, companyId, cancelled, setStatus, navigate);
    return;
  }

  if (type === 'signup' || type === 'email' || type === 'email_change' || type === 'email_confirmation' || !type || type === 'magiclink') {
    await routeAdmin(user, companyId, cancelled, setStatus, navigate);
    return;
  }

  setStatus('Checking your account…');

  if (role === 'worker') { if (!cancelled) navigate('/worker-today', { replace: true }); return; }
  if (role === 'site_manager') { if (!cancelled) navigate('/site-manager-dashboard', { replace: true }); return; }
  if (role === 'client') { if (!cancelled) navigate('/client-dashboard', { replace: true }); return; }

  await routeAdmin(user, companyId, cancelled, setStatus, navigate);
}

/**
 * Routes an admin user based solely on the companies table.
 * All new signups go to /setup-flow-company-details (single unified flow).
 */
async function routeAdmin(user, companyId, cancelled, setStatus, navigate) {
  setStatus('Checking your subscription…');

  // Clear any stale signup_source — no longer used for routing
  localStorage.removeItem('signup_source');

  try {
    // Try to find existing company
    let company = null;
    const { data: companyData } = await companyService?.getUserCompany();
    company = companyData;

    // If no company exists yet, create a minimal one.
    // Retry up to 3 times — user_profiles trigger may not have committed yet.
    if (!company?.id) {
      const email = user?.email || '';
      for (let attempt = 0; attempt < 3; attempt++) {
        const { error: createErr } = await companyService?.createCompany({
          companyName: email?.split('@')?.[0] || 'My Company',
          registrationNumber: null,
          addressLine1: 'TBC',
          addressLine2: null,
          city: 'TBC',
          county: null,
          postcode: 'TBC',
          contactPhone: '',
          contactEmail: email,
        });
        if (!createErr) break;
        console.warn(`[routeAdmin] createCompany attempt ${attempt + 1} failed:`, createErr?.message);
        if (attempt < 2) await new Promise(r => setTimeout(r, 1500));
      }
      // Re-fetch after creation
      const { data: newCompany } = await companyService?.getUserCompany();
      company = newCompany;
    }

    if (company?.id) {
      const subStatus = company?.subscription_status;
      const isActive = subStatus === 'active' || subStatus === 'trialing';
      const isSetupDone = company?.setup_complete === true;

      if (isActive && isSetupDone) {
        console.log('[routeAdmin] subscription active + setup complete → /admin-dashboard');
        if (!cancelled) navigate('/admin-dashboard', { replace: true });
      } else {
        // All new signups go to company details first (single unified flow)
        console.log('[routeAdmin] new signup → /setup-flow-company-details');
        if (!cancelled) navigate('/setup-flow-company-details', { replace: true, state: { fromAuthCallback: true, verifiedUser: user, newSignup: true } });
      }
    } else {
      // No company found — go to company details
      if (!cancelled) navigate('/setup-flow-company-details', { replace: true, state: { fromAuthCallback: true, verifiedUser: user, newSignup: true } });
    }
  } catch (_) {
    if (!cancelled) navigate('/setup-flow-company-details', { replace: true, state: { fromAuthCallback: true, verifiedUser: user, newSignup: true } });
  }
}

export default AuthCallback;
